/**
 * Single source of truth for region codes used across the Trending page, the
 * public Show detail page, the hero "Trending in <region>" card, etc.
 *
 *   - `REGION_CODES`        — canonical 6-region tuple (5 countries + global)
 *   - `REGION_LABEL_LONG`   — "the United States" / "New Zealand" / "the world"
 *   - `REGION_LABEL_SHORT`  — "the US" / "New Zealand" / "the world"
 *   - `REGION_NAME`         — pure country name for buttons ("United States", "Global")
 *   - `REGION_CC`           — ISO-3166 alpha-2 used by flagcdn.com (UK → gb)
 *   - `FLAG_EMOJI`          — fallback unicode flags (Windows may render as text)
 *   - `flagImgUrl(code, w)` — helper to build flagcdn.com URLs (null for global)
 *   - `normaliseRegion`     — case-insensitive input → canonical code or null
 *   - `isValidRegion`       — boolean helper
 */

export const REGION_CODES = ['NZ', 'AU', 'US', 'UK', 'CA', 'global'];

export const REGION_LABEL_LONG = {
  NZ: 'New Zealand',
  AU: 'Australia',
  US: 'the United States',
  UK: 'the United Kingdom',
  CA: 'Canada',
  global: 'the world',
};

export const REGION_LABEL_SHORT = {
  NZ: 'New Zealand',
  AU: 'Australia',
  US: 'the US',
  UK: 'the UK',
  CA: 'Canada',
  global: 'the world',
};

export const REGION_NAME = {
  NZ: 'New Zealand',
  AU: 'Australia',
  US: 'United States',
  UK: 'United Kingdom',
  CA: 'Canada',
  global: 'Global',
};

export const REGION_CC = {
  NZ: 'nz',
  AU: 'au',
  US: 'us',
  UK: 'gb',
  CA: 'ca',
};

export const FLAG_EMOJI = {
  NZ: '🇳🇿',
  AU: '🇦🇺',
  US: '🇺🇸',
  UK: '🇬🇧',
  CA: '🇨🇦',
  global: '🌍',
};

const FLAGCDN_BASE = 'https://flagcdn.com';

export const flagImgUrl = (code, widthPx = 40) => {
  const cc = REGION_CC[code];
  if (!cc) return null;
  return `${FLAGCDN_BASE}/w${widthPx}/${cc}.png`;
};

export const flagImgSrcSet = (code) => {
  const cc = REGION_CC[code];
  if (!cc) return null;
  return `${FLAGCDN_BASE}/w40/${cc}.png 1x, ${FLAGCDN_BASE}/w80/${cc}.png 2x`;
};

const VALID_SET = new Set(REGION_CODES);

export const isValidRegion = (code) => VALID_SET.has(code);

export const normaliseRegion = (raw) => {
  if (!raw) return null;
  const upper = String(raw).toUpperCase();
  if (upper === 'GLOBAL') return 'global';
  return isValidRegion(upper) ? upper : null;
};

/** Build the canonical /trending or /trending/:region URL. */
export const trendingPath = (region) =>
  region === 'global' ? '/trending' : `/trending/${region}`;

/** Build a canonical /show/:tmdbId URL, attaching ?region= when not global. */
export const showPath = (tmdbId, region) =>
  region && region !== 'global'
    ? `/show/${tmdbId}?region=${region}`
    : `/show/${tmdbId}`;
